import Quagga, { QuaggaJSCodeReader, QuaggaJSResultObject } from '@ericblade/quagga2';
import React, { useCallback, useLayoutEffect } from 'react';

import { ScanCodeReadersTypes } from '../../../../../../../../../interfaces/Scanner';
import QrCodeReader from './QrCodeReader';

Quagga.registerReader(`qr_reader`, QrCodeReader);

const defaultConstraints = {
  width: 640,
  height: 480,
  area: {
    // defines rectangle of the detection/localization area
    top: `0%`, // top offset
    right: `0%`, // right offset
    left: `0%`, // left offset
    bottom: `0%`, // bottom offset
  },
};

const defaultLocatorSettings = {
  patchSize: `medium`,
  halfSample: true,
  debug: {
    showCanvas: false,
    showPatches: false,
    showFoundPatches: false,
    showSkeleton: false,
    showLabels: false,
    showPatchLabels: false,
    showRemainingPatchLabels: false,
    boxFromPatches: {
      showTransformed: false,
      showTransformedBox: false,
      showBB: false,
    },
  },
};

const defaultDecoders = [`ean_reader`] as ScanCodeReadersTypes[];

interface CodesScannerProps {
  onDetected: (code: string) => void;
  scannerRef: React.RefObject<any>;
  onScannerReady?: () => void;
  cameraId?: string;
  facingMode?: string;
  constraints?: {
    width?: number;
    height?: number;
    area?: {
      top: string;
      right: string;
      left: string;
      bottom: string;
    };
  };
  locator?: {
    patchSize?: string;
    halfSample?: boolean;
  };
  numOfWorkers?: number;
  decoders?: ScanCodeReadersTypes[];
  locate?: boolean;
}

export const CodesScanner = ({
  onDetected,
  scannerRef,
  onScannerReady,
  cameraId,
  facingMode,
  constraints = defaultConstraints,
  locator = defaultLocatorSettings,
  numOfWorkers = navigator.hardwareConcurrency || 0,
  decoders = defaultDecoders,
  locate = true,
}: CodesScannerProps) => {
  const onDetectedHandler = useCallback(
    (result: QuaggaJSResultObject) => {
      console.info(`onDetectedHandler`, result);
      const code = result.codeResult.code;
      if ((code ?? ``) === `` || !onDetected) {
        return;
      }
      onDetected(code!);
    },
    [onDetected]
  );

  useLayoutEffect(() => {
    Quagga.init(
      {
        inputStream: {
          type: `LiveStream`,
          constraints: {
            ...constraints,
            ...(decoders.includes(`qr_reader`)
              ? {
                  aspectRatio: { ideal: 1 },
                  ...(cameraId === `default` ? { facingMode: `environment` } : { deviceId: cameraId }),
                }
              : {
                  ...(cameraId && { deviceId: cameraId }),
                  ...(!cameraId && { facingMode }),
                }),
          },
          target: scannerRef.current,
        },
        locator,
        numOfWorkers,
        frequency: 20, // default: 60
        decoder: {
          readers: decoders as QuaggaJSCodeReader[],
        },
        locate,
      },
      (err) => {
        if (err) {
          return console.log(`Error starting Quagga:`, err);
        }
        if (scannerRef && scannerRef.current) {
          Quagga.start();
          if (onScannerReady) {
            onScannerReady();
          }
        }

        Quagga.start();
        return null;
      }
    );

    Quagga.onDetected(onDetectedHandler);
    return () => {
      Quagga.offDetected(onDetectedHandler);

      Quagga.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, onDetected, onScannerReady, scannerRef, onDetectedHandler, constraints, locator, decoders, locate]);

  return null;
};
